import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock, DollarSign, Check, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingService, setProcessingService] = useState(null);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleBookService = async (service) => {
    if (!user) {
      toast.info("Please sign in to book a service");
      navigate("/login", { state: { from: "/services" } });
      return;
    }

    setProcessingService(service.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          service_id: service.id,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process booking. Please try again.");
    } finally {
      setProcessingService(null);
    }
  };

  const getPriceLabel = (priceType) => {
    const labels = {
      per_session: "per session",
      package: "package",
      annual: "per year",
      starting_at: "starting at",
      per_signing: "per signing",
      info_page: "starting at"
    };
    return labels[priceType] || priceType;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Our Services</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Comprehensive Business Solutions
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            From startup formation to ongoing support, we offer the services you need to build and grow a successful business.
          </p>
        </div>
      </section>

      {/* Special Offer Banner */}
      <section className="bg-[#9D9045] py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-white text-center">
          <span className="text-lg font-medium">Money Management Coaching now includes FREE "Stop Living Paycheck to Paycheck" Ebook!</span>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div 
                key={service.id} 
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                data-testid={`service-item-${service.id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image_url} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-semibold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3">
                    {service.description}
                  </p>
                  
                  {service.price_type !== "partner" && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-[#9D9045]" />
                        <span className="text-2xl font-bold text-[#1E3A5F]">
                          {service.price?.toFixed(0)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {getPriceLabel(service.price_type)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {service.price_type === "partner" && service.affiliate_url ? (
                    <a 
                      href={service.affiliate_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button 
                        className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white"
                      >
                        Visit Partner Site <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  ) : service.info_url ? (
                    <Link to={service.info_url} className="block">
                      <Button 
                        className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white"
                      >
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  ) : (
                    <Button 
                      onClick={() => handleBookService(service)}
                      disabled={processingService === service.id}
                      className="w-full bg-[#1E3A5F] hover:bg-[#162c47] text-white"
                      data-testid={`book-service-${service.id}`}
                    >
                      {processingService === service.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Book Now <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Choose OMW?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Check, title: "Veteran Owned", desc: "Veteran-Owned Small Business" },
              { icon: Check, title: "NNA Certified", desc: "National Notary Association certified professionals" },
              { icon: Clock, title: "Flexible Hours", desc: "Weekend and after-hours appointments available" },
              { icon: DollarSign, title: "Free Consultation", desc: "Free discovery consultations for new clients" }
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="h-14 w-14 rounded-full bg-[#9D9045]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-[#9D9045]" />
                </div>
                <h4 className="font-semibold text-[#1E3A5F] mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Not Sure Where to Start?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Schedule a free discovery consultation and let us help you find the right services for your business needs.
          </p>
          <Link to="/contact">
            <Button className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg" data-testid="services-contact-btn">
              Schedule Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
